import {
  BaseQueryFn,
  createApi, FetchArgs,
  fetchBaseQuery, FetchBaseQueryMeta
} from "@reduxjs/toolkit/query/react";
import { msalInstance } from "../../../utils/authentication";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { AdminState } from "../../../stores/AdminStore";
import { setAccessToken, signOut } from "../../../stores/AuthSlice";
import toast from "react-hot-toast/headless";
import { GetErrorMessage, isFetchArgs } from "../../../utils/apiUtils";
import { t } from "i18next";
import {
  BaseQueryApi,
  QueryReturnValue
} from "@reduxjs/toolkit/dist/query/baseQueryTypes";

interface CustomFetchArgs extends FetchArgs {
  successMessage?: string;
  skipToast?: boolean;
}

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_UNLEASHED_API_URL}/adminV2`,
  prepareHeaders: (headers, { getState }) => {
    const {auth: {accessToken}} = getState() as AdminState;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const refreshTokenQuery = (
  args: string | CustomFetchArgs,
  api : BaseQueryApi,
  // fetchBaseQuery requires {} as the param
  extraOptions: {}
) => {
  return async () => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const authResult = await msalInstance.acquireTokenSilent({
        scopes: [
          "openid",
          "offline_access",
          import.meta.env.VITE_AZURE_B2C_APP_ID
        ]
      });

      if (authResult.accessToken) {
        api.dispatch(setAccessToken(authResult.accessToken));
        result = await baseQuery(args, api, extraOptions);
      } else {
        msalInstance.logoutRedirect().then(() => api.dispatch(signOut()));
      }
    }

    return result;
  };
};

const isPlainTextResponse = (queryResult: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>) =>
  queryResult.meta?.response?.headers.get("content-type")?.includes("text/plain");

const adminBaseQuery: BaseQueryFn<string | CustomFetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {

  const query = refreshTokenQuery(args, api, extraOptions);

  if (!isFetchArgs(args) || args.method === "GET") {
    const result = await query();

    if (result.error) {
      toast.error(GetErrorMessage(result.error,isPlainTextResponse(result)));
    }

    return result;
  } else {
    const promise = async () => {
      const result = await query();

      if (result.error) {
        throw GetErrorMessage(result.error,isPlainTextResponse(result));
      }

      return result;
    };

    if (args.skipToast) {
      return promise();
    }

    return toast.promise(promise(), {
      loading: args.method === "DELETE"
        ? `${t`Deleting`}...`
        : `${t`Saving`}...`,
      success:  args.successMessage || `${t`Success`}!`,
      error: (e) => e,
    });
  }
};

export const AdminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: adminBaseQuery,
  tagTypes: [
    "Brands",
    "Locations",
    "Permissions",
    "Users",
    "Roles",
    "Promotions",
    "ProductLevels",
    "ProductTypes",
    "Accounts",
    "Queues",
    "PendingRefunds",
    "Participants",
    "Memberships",
    "Releases",
    "ReasonCodes",
    "Enrollments",
    "Migrations",
    "RetentionDiscounts",
    "Reports",
    "CampPasses",
    "ItemFees",
    "AttractionTypes",
    "BookingPromotions",
    "PaymentMethods",
    "Documents",
    "AccountNotes",
    "AccountMakeups",
    "Tickets",
    "AccountAttendance",
    "PosConfiguration",
    "Refund",
    "BillingHistory",
    "LocationDocuments",
    "CafeProducts",
    "Merchandise",
    "Categories",
    "Bundles",
    "Products",
    "PartyInvoice",
    "EventTemplates",
    "ClassCheckin",
    "ProductTemplates",
    "BrandSettings",
    "RateChangeRequests"
  ],
  endpoints: () => ({}),
});
